import axios from 'axios';
import building from '../store/modules/building';
import store from '@/store/store'
import { attachAccessToken, requestTicket } from './ticket-api'

const baseURL = process.env.VUE_APP_APP_URL;

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Flag กันการยิง Refresh Token ซ้ำซ้อน (Concurrency Issue) ---
// กรณีหน้าเว็บโหลดพร้อมกัน 5 API แล้ว Token หมดอายุ
// เราต้องการให้มัน Refresh แค่ครั้งเดียว ไม่ใช่ยิงไป 5 ครั้ง
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor: แนบ Token
instance.interceptors.request.use(
  (config) => attachAccessToken(config),
  (error) => Promise.reject(error)
);

// Response Interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ถ้า Error 401 และยังไม่ได้ Retry (และไม่ใช่ path /auth/google เพราะการ login ไม่ควรเรียก refresh token)
    if (error.response && error.response.status === 401 && !originalRequest._retry && originalRequest.url !== '/auth/google') {

      // ป้องกันการ Refresh ซ้อนกันถ้ามีหลาย Request เข้ามาพร้อมกัน
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return instance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // ยิงไปขอ Token ใหม่
        const response = await instance.post("/auth/refresh-token", {
          refreshToken: refreshToken,
        });

        const { accessToken } = response.data;

        // 1. เก็บ Token ใหม่
        localStorage.setItem("accessToken", accessToken);

        // 2. อัปเดต defaults
        instance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        // 3. Process คิวที่รออยู่ (สำหรับ Request อื่นๆ ที่เข้ามาพร้อมกัน)
        processQueue(null, accessToken);

        // 4. ทำ request เดิมซ้ำ
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return instance(originalRequest);

      } catch (refreshError) {
        // กรณี Refresh ไม่ผ่าน
        processQueue(refreshError, null);
        store.dispatch("oAuth/logout");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


export default {


  auth(method, data, configs) {
    switch (method) {
      case 'google':
        return instance.post("/auth/google", data);
      case 'mockLogin':
        return instance.post("/auth/mockLogin", data);
      case 'refresh-token':
        return instance.post("/auth/refresh-token", data);
      default:
        throw new Error(`Auth method ${method} not supported`);
    }
  },

  building(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/building");
      case 'post':
        return instance.post("/building", data);
      case 'put':
        return instance.put("/building" + (configs?.urlAppend || ""), data);
      case 'delete':
        return instance.delete("/building" + (configs?.urlAppend || ""));
      default:
        break;
    }
  },

  workManagement(method, data, configs = {}) {
    return instance.request({ method, url: '/work-management' + (configs.urlAppend || ''), data });
  },
  category(method, data, configs) {
    switch (method) {
      case 'get':
        return instance.get("/category" + (configs?.urlAppend || ""));
      case 'post':
        return instance.post("/category", data);
      case 'put':
        return instance.put("/category", data);
      case 'delete':
        return instance.delete("/category" + (configs?.urlAppend || ""));
      default:
        break;
    }
  },

  user(method, data, configs) {
    const path = "/user" + (configs?.urlAppend || "");
    switch (method) {
      case "get":
        return instance.get(path, { params: configs?.params });
      case "post":
        return instance.post(path, data);
      case "put":
        return configs?.methodOverride === "patch"
          ? instance.patch(path, data)
          : instance.put(path, data);
      case "delete":
        return instance.delete(path, { data });
      default:
        throw new Error(`User method ${method} not supported`);
    }
  },

  department(method, data, configs) {
    const path = "/departments" + (configs?.urlAppend || "");
    switch (method) {
      case "get":
        return instance.get(path, { params: configs?.params });
      case "post":
        return instance.post(path, data);
      case "put":
        return instance.put(path, data);
      case "delete":
        return instance.delete(path, { data });
      default:
        throw new Error(`Department method ${method} not supported`);
    }
  },

  requesterOrganizationSync(method, data, configs) {
    if (method === 'preview') return instance.post('/integrations/blendata/organization-units/preview', {});
    if (method === 'apply') return instance.post('/integrations/blendata/organization-units/controlled-apply', data);
    if (method === 'getSettings') return instance.get('/integrations/blendata/organization-units/settings');
    if (method === 'saveSettings') return instance.put('/integrations/blendata/organization-units/settings', data);
    if (method === 'check') return instance.post('/integrations/blendata/organization-units/check', {});
    if (method === 'syncNow') return instance.post('/integrations/blendata/organization-units/sync-now', {});
    if (method === 'manualApply') return instance.post('/integrations/blendata/organization-units/manual-apply', data);
    if (method === 'getRecentUpdates') return instance.get('/integrations/blendata/organization-units/recent-updates');
    if (method === 'revert') return instance.post('/integrations/blendata/organization-units/revert', data);
    if (method === 'previewSchedule') return instance.post('/integrations/blendata/organization-units/schedule-preview', data);
    if (method === 'getPendingReview') return instance.get('/integrations/blendata/organization-units/pending-review', { params: configs?.params });
    if (method === 'approve') return instance.post('/integrations/blendata/organization-units/approve', data);
    if (method === 'postpone') return instance.post('/integrations/blendata/organization-units/postpone', data);
    if (method === 'resume') return instance.post('/integrations/blendata/organization-units/resume', data);
    if (method === 'verifyHr') return instance.post('/integrations/blendata/organization-units/verify-hr', data);
    if (method === 'keepDefault') return instance.post('/integrations/blendata/organization-units/keep-default', data);
    if (method === 'getHistory') return instance.get('/integrations/blendata/organization-units/history', { params: configs?.params });
    throw new Error(`Requester organization sync method ${method} not supported`);
  },

  ticket(method, data, configs) {
    return requestTicket(instance, method, data, configs);
  },

  notification(method, data, configs) {
    const path = "/notifications" + (configs?.urlAppend || "");
    switch (method) {
      case "get":
        return instance.get(path, { params: configs?.params });
      case "patch":
        return instance.patch(path, data);
      default:
        throw new Error(`Notification method ${method} not supported`);
    }
  },
};

export { instance };

